import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  Grid,
  Fab,
  Switch,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CampaignOutlined as AnnouncementIcon,
  CheckBoxOutlined as SelectedIcon,
  FileDownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const truncateMessage = (message, max = 90) => {
  if (!message) return "No message";
  return message.length > max ? `${message.slice(0, max)}…` : message;
};

const AnnouncementCard = ({
  item,
  isSelected,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  canEdit,
  canDelete,
}) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      mb: 1.25,
      border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
      boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
      overflow: "hidden",
      opacity: item.active === false ? 0.72 : 1,
    }}
  >
    <Box sx={{ px: 1.25, py: 0.875 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "24px 1fr auto auto auto",
          alignItems: "center",
          columnGap: 0.35,
          mb: 0.35,
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggleSelect(item)}
          sx={{
            p: 0,
            color: BRAND.muted,
            "&.Mui-checked": { color: ORANGE },
          }}
          inputProps={{ "aria-label": `Select ${item.title}` }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: NAVY,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {item.title || "Untitled"}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onView(item)}
          aria-label="View announcement"
          sx={{ p: 0, color: "#2196f3", width: 32, height: 32 }}
        >
          <VisibilityIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        {canEdit && (
          <IconButton
            size="small"
            onClick={() => onEdit(item)}
            aria-label="Edit announcement"
            sx={{ p: 0, color: ORANGE, width: 32, height: 32 }}
          >
            <EditIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            size="small"
            onClick={() => onDelete(item.id)}
            aria-label="Delete announcement"
            sx={{ p: 0, color: "#d32f2f", width: 32, height: 32 }}
          >
            <DeleteIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ pl: "calc(24px + 6px)" }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: BRAND.text,
            lineHeight: 1.4,
            mb: 0.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {truncateMessage(item.message)}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
            {formatDate(item.created_at)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.625rem", color: BRAND.muted, fontWeight: 600 }}>
              Active
            </Typography>
            <Switch
              size="small"
              checked={Boolean(item.active)}
              onChange={() => onToggleActive(item)}
              disabled={!canEdit}
              color="primary"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

const AnnouncementMobileView = ({
  announcements,
  searchTerm,
  setSearchTerm,
  selectedRows,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onAdd,
  onExport,
  canCreate,
  canEdit,
  canDelete,
  canExport,
}) => {
  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => String(row.id))),
    [selectedRows]
  );

  const stats = useMemo(
    () => ({
      total: announcements.length,
      active: announcements.filter((item) => item.active !== false).length,
    }),
    [announcements]
  );

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        Announcements
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        Publish updates and notices for your organization
      </Typography>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<AnnouncementIcon sx={{ fontSize: "1rem" }} />}
            label="Total"
            value={stats.total}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
          />
        </Grid>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<AnnouncementIcon sx={{ fontSize: "1rem" }} />}
            label="Active"
            value={stats.active}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
          />
        </Grid>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<SelectedIcon sx={{ fontSize: "1rem" }} />}
            label="Selected"
            value={selectedRows.length}
            iconBg="rgba(230,90,0,0.12)"
            iconColor={ORANGE}
          />
        </Grid>
      </Grid>

      {canExport && selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            bgcolor: BRAND.orangeLight,
            borderRadius: "12px",
            border: `1px solid ${ORANGE}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY }}>
            {selectedRows.length} selected
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onExport}
            sx={{
              border: "none",
              borderRadius: "10px",
              bgcolor: ORANGE,
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.75rem",
              px: 1.5,
              py: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
          >
            <ExportIcon sx={{ fontSize: "0.95rem" }} />
            Export
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        placeholder="Search announcements..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": { borderColor: ORANGE },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {announcements.length > 0 ? (
        announcements.map((item) => (
          <AnnouncementCard
            key={item.id}
            item={item}
            isSelected={selectedIds.has(String(item.id))}
            onToggleSelect={onToggleSelect}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleActive={onToggleActive}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <AnnouncementIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No announcements yet
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            {canCreate
              ? 'Tap + to publish your first notice.'
              : "No announcements are available."}
          </Typography>
        </Box>
      )}

      {canCreate && (
        <Fab
          color="primary"
          aria-label="Add announcement"
          onClick={onAdd}
          size="medium"
          sx={{
            position: "fixed",
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
            right: 20,
            bgcolor: ORANGE,
            "&:hover": { bgcolor: BRAND.orangeHover },
            boxShadow: "0 4px 16px rgba(230, 90, 0, 0.35)",
            zIndex: 1002,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default AnnouncementMobileView;
